import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '../../../components/AppIcon';

const ROITimelineChart = ({ totalPayroll, costSavings, roiPercentage }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-PG', {
      style: 'currency',
      currency: 'PGK',
      minimumFractionDigits: 0
    })?.format(value || 0);
  };

  // Generate cumulative savings data
  const generateTimelineData = () => {
    const months = 12;
    const monthlySavings = costSavings / 12;
    const data = [];

    for (let i = 1; i <= months; i++) {
      data?.push({
        month: `Month ${i}`,
        cumulativeSavings: monthlySavings * i,
        projectedSavings: monthlySavings * i * 1.1 // 10% growth projection
      });
    }

    return data;
  };

  const timelineData = generateTimelineData();
  const breakEvenMonth = Math.ceil(12 / (roiPercentage / 100 + 1));

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">ROI Timeline & Cumulative Savings</h2>
          <p className="text-sm text-gray-600 mt-1">Savings progression with break-even analysis</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-gray-600">Break-even Point</p>
            <p className="text-sm font-semibold text-blue-600">Month {breakEvenMonth}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600">Annual Projection</p>
            <p className="text-sm font-semibold text-green-600">{formatCurrency(costSavings * 1.1)}</p>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={timelineData}>
          <defs>
            <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="month"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            tickFormatter={formatCurrency}
          />
          <Tooltip
            formatter={(value) => formatCurrency(value)}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px'
            }}
          />
          <ReferenceLine
            x={`Month ${breakEvenMonth}`}
            stroke="#f59e0b"
            strokeDasharray="3 3"
            label={{ value: 'Break-even', position: 'top', fill: '#f59e0b', fontSize: 12 }}
          />
          <Area
            type="monotone"
            dataKey="cumulativeSavings"
            stroke="#3b82f6"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorSavings)"
            name="Cumulative Savings"
          />
          <Area
            type="monotone"
            dataKey="projectedSavings"
            stroke="#10b981"
            strokeWidth={2}
            strokeDasharray="5 5"
            fillOpacity={1}
            fill="url(#colorProjected)"
            name="Projected Savings"
          />
        </AreaChart>
      </ResponsiveContainer>
      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Current Savings</span>
          </div>
          <p className="text-lg font-bold text-blue-900">{formatCurrency(costSavings)}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-900">Projected Annual</span>
          </div>
          <p className="text-lg font-bold text-green-900">{formatCurrency(costSavings * 1.1)}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-900">ROI Percentage</span>
          </div>
          <p className="text-lg font-bold text-purple-900">{roiPercentage?.toFixed(1)}%</p>
        </div>
      </div>
    </div>
  );
};

export default ROITimelineChart;